/* eslint-disable camelcase */
import { BodyText, Icon } from '@postscript/components';
import { getSystemMessageIcon } from 'components/responses/utils/messages';
import moment from 'moment';
import React, { useState } from 'react';
import styled from 'styled-components';

interface ProfileEventProps {
  message: {
    text: string;
    direction?: string;
    eventData?: {
      description?: string;
      public_metadata?: string;
      [key: string]: any;
    };
    created_at?: string;
    [key: string]: any;
  };
}

const Container = styled.div`
  display: flex;
  align-items: center;
  background: transparent;
  padding: 8px 0;
  gap: 8px;
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-color-dim);
  min-width: 20px;
  max-width: 20px;
  height: 20px;
`;

const ContentWrapper = styled.div`
  flex: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const MainContent = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 1;
  min-width: 0;
`;

const HeaderTimestamp = styled.div`
  margin-left: auto;
  white-space: nowrap;
`;

const ClickableUrl = styled.a`
  color: var(--text-color);
  text-decoration: underline;
  cursor: pointer;
  word-break: break-all;
  overflow-wrap: break-word;

  &:hover {
    color: var(--primary-color);
  }
`;

const MetadataRow = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 4px;
  gap: var(--spacing-1);

  &:last-child {
    margin-bottom: 0;
  }
`;

const MetadataLabel = styled.span`
  font-weight: 600;
  color: var(--text-color-dim);
`;

const MetadataValue = styled.span`
  color: var(--text-color);
`;

const ProfileEvent: React.FC<ProfileEventProps> = ({ message }) => {
  const [expanded, setExpanded] = useState(false);

  const isLinkClickEvent = (): boolean => {
    return message.type === 'profile.subscriber.link_clicked';
  };

  const formatMetadata = () => {
    if (!isLinkClickEvent() || !message.eventData) return null;

    // eslint-disable-next-line camelcase
    const { message_type, message_name, flow_type } = message.eventData;
    const metadata = [];

    // eslint-disable-next-line camelcase
    if (message_type) {
      // eslint-disable-next-line camelcase
      if (message_name) {
        // eslint-disable-next-line camelcase
        metadata.push({
          label: 'Message',
          // eslint-disable-next-line camelcase
          value: `${message_name} (${message_type})`,
        });
      } else {
        // eslint-disable-next-line camelcase
        metadata.push({
          label: 'Type',
          // eslint-disable-next-line camelcase
          value: message_type,
        });
      }
    }

    // eslint-disable-next-line camelcase
    if (flow_type) {
      // eslint-disable-next-line camelcase
      metadata.push({ label: 'Flow Type', value: flow_type });
    }

    return metadata;
  };

  const formatTimestamp = (timestamp: any): string | null => {
    try {
      const parsedMoment = moment(timestamp, 'YYYY-MM-DD HH:mm:ss', true);
      return parsedMoment.isValid()
        ? parsedMoment.format('MM/DD/YY • h:mm A')
        : null;
    } catch (error) {
      console.warn(`Error parsing timestamp:`, timestamp, error);
      return null;
    }
  };

  const getDisplayText = (): {
    displayText: string;
    iconText: string;
    timestamp: string | null;
  } => {
    const { description, public_metadata: publicMetadata } =
      message.eventData || {};

    const timestamp = message.createdAt
      ? formatTimestamp(message.createdAt)
      : null;

    if (!description || !publicMetadata) {
      return { displayText: message.text, iconText: message.text, timestamp };
    }

    return { displayText: description, iconText: description, timestamp };
  };

  const { displayText, iconText, timestamp } = getDisplayText();
  const resolvedIcon = getSystemMessageIcon(iconText);
  const showAccordion = !!(
    message.eventData?.description && message.eventData?.public_metadata
  );
  const metadata = formatMetadata();

  return (
    <>
      <button
        type="button"
        style={{
          background: 'none',
          border: 'none',
          width: '100%',
          padding: 0,
          margin: 0,
          cursor: showAccordion ? 'pointer' : 'default',
          display: 'block',
          textAlign: 'inherit',
        }}
        aria-expanded={showAccordion ? expanded : undefined}
        aria-controls={showAccordion ? 'profile-event-details' : undefined}
        onClick={showAccordion ? () => setExpanded((prev) => !prev) : undefined}
        tabIndex={0}
        disabled={!showAccordion}
      >
        <Container>
          <IconWrapper>
            <Icon
              color="var(--text-color-dim)"
              component={resolvedIcon}
              size={16}
            />
          </IconWrapper>
          <ContentWrapper>
            <MainContent>
              <BodyText
                color="var(--text-color)"
                size="x-small"
                style={{ fontWeight: 500 }}
              >
                {displayText}
              </BodyText>
            </MainContent>
            {timestamp && (
              <HeaderTimestamp>
                <BodyText color="var(--text-color-dim)" size="x-small">
                  {timestamp}
                </BodyText>
              </HeaderTimestamp>
            )}
          </ContentWrapper>
          {showAccordion && (
            <span
              style={{
                marginLeft: 'auto',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{
                  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s',
                }}
              >
                <path
                  d="M6 8L10 12L14 8"
                  stroke="#222"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          )}
        </Container>
      </button>
      {showAccordion && expanded && (
        <div
          id="profile-event-details"
          style={{
            background: 'transparent',
            marginTop: '4px',
            padding: '0 0 0 28px', // left padding to align with text
            maxWidth: '100%',
            overflow: 'hidden',
          }}
        >
          {isLinkClickEvent() && message.eventData?.public_metadata ? (
            <ClickableUrl
              href={message.eventData.public_metadata}
              target="_blank"
              rel="noopener noreferrer"
            >
              <BodyText
                color="var(--text-color)"
                size="x-small"
                style={{ fontWeight: 600 }}
              >
                {message.eventData.public_metadata}
              </BodyText>
            </ClickableUrl>
          ) : (
            <BodyText
              color="var(--text-color)"
              size="x-small"
              style={{ fontWeight: 600 }}
            >
              {message.eventData?.public_metadata}
            </BodyText>
          )}

          {isLinkClickEvent() && metadata && metadata.length > 0 && (
            <div style={{ marginTop: '8px' }}>
              {metadata.map((item) => (
                <MetadataRow key={`${item.label}-${item.value}`}>
                  <MetadataLabel>
                    <BodyText size="x-small">{item.label}:</BodyText>
                  </MetadataLabel>
                  <MetadataValue>
                    <BodyText size="x-small">{item.value}</BodyText>
                  </MetadataValue>
                </MetadataRow>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default ProfileEvent;
